import React, { useState, useEffect } from 'react';
import ConsentTabTriage from './ConsentTabTriage';
import ConsentTabNetworkEform from './ConsentTabNetworkEform';

/**
 * ConsentTab - Router/Wrapper Component
 * 
 * CRITICAL ARCHITECTURAL NOTE:
 * ============================
 * The consent form has TWO COMPLETELY DIFFERENT IMPLEMENTATIONS based on report type:
 * 
 * 1. TRIAGE FORMS (Report Types: 2002, 2003)
 *    - Patient-facing consent/intake form
 *    - Captures patient consent (ConsentGained yes/no)
 *    - Captures data protection confirmation
 *    - Captures GP contact info for safety purposes
 *    - Fields: ConsentGained, ConsentGainedNotes, DataProtectionCompleted, GpName, GpSurgery, GpAddress
 * 
 * 2. NETWORK EFORM FORMS (Report Types: 2010, 2011)
 *    - Administrative/clinical confirmation form
 *    - Requires clinician verification and sign-off
 *    - Confirms assessment details and dates
 *    - Does NOT gather patient consent (that's for Triage only)
 *    - Does NOT capture GP info (that's for Triage only)
 *    - Fields: CaseReference, FullName, BirthDate, DetailsChecked, SignedBy, AssessmentDate
 * 
 * The Swagger API explicitly confirms this separation with type-specific endpoint summaries:
 * - ConsentGained endpoint: "Supported by report types: 2002 (Triage), 2003 (Triage Follow Up)"
 * - DetailsChecked endpoint: "Supported by report types: 2010 (Network Eform Initial), 2011 (Network Eform Follow Up)"
 * - GpName/Surgery/Address endpoints: "Supported by report types: 2002, 2003"
 * 
 * This component routes to the correct implementation based on the reportType prop.
 * Both implementations share the same endpoint base (/api/PatientCaseReports/{reportId}/Consent)
 * but use completely different field sets.
 */
const ConsentTab = ({ reportId, api, patientInfo, onSave, reportType , isPublished}) => {
  const [loading, setLoading] = useState(true);
  const [reportInfo, setReportInfo] = useState(null);
  const [error, setError] = useState(null);

  // Determine the actual report type if not provided as prop
  useEffect(() => {
    const determineReportType = async () => {
      try {
        setLoading(true);
        // Try to fetch report info to get the report type
        const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}`);
        if (response.ok) {
          const data = await response.json();
          setReportInfo(data);
        }
      } catch (err) {
        console.error('Error loading report info:', err);
        // Continue anyway - will use reportType prop if available
      } finally {
        setLoading(false);
      }
    };

    if (!reportType && reportId) {
      determineReportType();
    } else {
      setLoading(false);
    }
  }, [reportId, reportType, api]);

  // Determine which component to render based on report type
  const actualReportType = reportType || reportInfo?.reportTypeId;

  // Triage report types: 2002, 2003
  const isTriageForm = actualReportType === 2002 || actualReportType === 2003;

  // Network Eform types: 2010, 2011
  const isNetworkEformForm = actualReportType === 2010 || actualReportType === 2011;

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading consent form...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-state">
        <p>{error}</p>
      </div>
    );
  }

  // Render the appropriate component based on report type
  if (isTriageForm) {
    return <ConsentTabTriage reportId={reportId} api={api} patientInfo={patientInfo} onSave={onSave} isPublished={isPublished} reportType={actualReportType} />;
  }

  if (isNetworkEformForm) {
    return <ConsentTabNetworkEform reportId={reportId} api={api} patientInfo={patientInfo} onSave={onSave} isPublished={isPublished} />;
  }

  // Unknown report type
  return (
    <div className="error-state">
      <h3>Unknown Report Type</h3>
      <p>Report type {actualReportType} is not supported for consent forms.</p>
      <p>Supported types: 2002 (Triage), 2003 (Triage Follow Up), 2010 (Network Eform Initial), 2011 (Network Eform Follow Up)</p>
    </div>
  );
};

export default ConsentTab;
